import { ApiTranslation } from '../api-translation';
import { LocalizedText } from '../localized-text';
import { CurrencyView } from '../currency-view';
import { BlockLoading } from '../fetch-status/block-loading';
import { EndStatus } from '../fetch-status/end-status';
import { PositionNothing } from '../fetch-status/position-nothing';
import { FormatAfterDate } from '../format/format-after-date';
import { OptionsPositionBadge } from '../options-positions-badge';
import { TextTooltip } from '../text-tooltip';
import { Separator } from '../ui/separator';
import { UploadedImage } from '../uploaded-image';
import { IS_DEBUG } from '@/client/confs/app';
import { useAppSettingStore } from '@/client/store/app-store';
import { useModelStore } from '@/client/store/model-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { useUserTrades } from '@/libs/react-sdk/api/use-user-trades';
import { OrderSide, UserOrderInfo } from '@/libs/sdk/api';
import useInViewport from 'ahooks/lib/useInViewport';
import { BasicTarget } from 'ahooks/lib/utils/domTarget';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';

export const TradeItemDT = ({
  label,
  value,
  decimal = 6,
  isChange = false,
  baseTokenAddress,
}: {
  label: string;
  value: any;
  decimal?: number;
  isChange?: boolean;
  baseTokenAddress?: string;
}) => {
  const isUp = Number(value) > -1 / Math.pow(10, 2);
  return (
    <div className="flex flex-col">
      <div className="text-3 leading-[normal] font-medium text-foreground/40">{label}</div>
      <div
        className={cn(
          'flex items-center space-x-.5',
          isChange ? (isUp ? 'text-[#3CBD2C]' : 'text-[#F65570]') : 'text-foreground'
        )}
      >
        <CurrencyView
          isChange={isChange}
          showIcon={true}
          value={value}
          decimal={decimal}
          precision={2}
          baseTokenAddress={baseTokenAddress}
          className={cn(
            'text-3 leading-[normal] font-medium',
            isChange ? (isUp ? 'text-[#3CBD2C]' : 'text-[#F65570]') : 'text-foreground'
          )}
        />
      </div>
    </div>
  );
};

type TradeItemProps = {
  className?: string;
  root?: BasicTarget<Element>;
  onViewChange?: (isInView: boolean) => void;
  order: UserOrderInfo;
};
export const TradeItem = ({ className, root, onViewChange, order }: TradeItemProps) => {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  const setTradeOpen = useModelStore(state => state.setTradeOpen);
  useInViewport(ref, {
    root,
    callback: entry => onViewChange?.(entry.isIntersecting),
  });
  const optionsAmount = useMemo(() => {
    if (order.side === OrderSide.deposit) {
      return Number(order.receiveAmount);
    }
    if (order.side === OrderSide.withdraw) {
      return Number(order.amount);
    }
    return 0;
  }, [order]);
  return (
    <div
      ref={ref}
      onClick={() => {
        navigate(`/event/${order.option.eventId}`);
      }}
      className={cn(
        'min-h-[137px] py-3 pl-4 pr-3 border border-foreground/10 rounded-4 hover:bg-foreground/5 cursor-pointer bg-card',
        className
      )}
    >
      <div className="flex items-center space-x-3">
        <UploadedImage fileKey={order.option.marketPicUrl} className="size-9 rounded-2" />
        <div className="space-y-1 flex-1">
          <div className="flex items-center justify-between">
            <TextTooltip
              tooltip={order.option.marketName}
              className=" font-medium line-clamp-3 text-left market-title-hover"
            >
              <LocalizedText i18nMap={order.option.nameI18n} fallback={order.option.marketName} />
            </TextTooltip>
          </div>
          {order.option && (
            <OptionsPositionBadge
              follow={false}
              options={[
                {
                  type: order.side == OrderSide.deposit ? 'buy' : 'sell',
                  marketAddress: order.option.marketAddress,
                  optionAddress: order.option.address,
                  label: order.option.description ?? order.option.name,
                  value: optionsAmount,
                  decimal: order.option.decimal,
                  amount: BigInt(optionsAmount),
                },
              ]}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setTradeOpen(true);
              }}
            />
          )}
        </div>
      </div>
      <Separator orientation="vertical" className="h-[1px] w-full my-4 bg-foreground/4 " />
      <div className="flex space-x-3 justify-between items-center">
        <div className="flex flex-col">
          <div className="text-3 leading-[normal] font-medium text-foreground/40">{t('type')}</div>
          <div>
            {order.side === OrderSide.deposit && t('buy')}
            {order.side === OrderSide.withdraw && t('sell')}
          </div>
        </div>
        <TradeItemDT
          label={t('price')}
          value={order.dealPrice}
          decimal={order.option.decimal}
          baseTokenAddress={order.option.baseTokenAddress}
        />
        <TradeItemDT
          label={t('value')}
          value={optionsAmount}
          decimal={order.option.decimal}
          baseTokenAddress={order.option.baseTokenAddress}
        />
        <div className="flex flex-col">
          <div className="text-3 leading-[normal] font-medium text-foreground/40">{t('date')}</div>
          <div>
            <FormatAfterDate time={order.timestamp} />
          </div>
        </div>
      </div>
    </div>
  );
};

type TradesProps = {
  uid: string;
  className?: string;
};
export const Trades = ({ uid, className }: TradesProps) => {
  const baseToken = useAppSettingStore(state => state.baseToken);
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useUserTrades(api, {
    page: 1,
    pageSize: 5,
    uid,
    baseTokenAddress: baseToken.address,
  });
  const orderList = useMemo(() => data?.pages.flatMap(item => item.orders) ?? [], [data]);
  const moreLastIndex = useMemo(() => orderList.length - 2, [orderList.length]);
  const loadMoreItems = async (isInView: boolean, index: number) => {
    if (isInView && index === moreLastIndex && hasNextPage && !isFetchingNextPage) {
      if (IS_DEBUG) console.log('load more history', uid, moreLastIndex, isInView, index);
      await fetchNextPage();
    }
  };
  return (
    <div className={cn('w-full', className)}>
      <div className="space-y-4">
        {orderList.map((item, index) => (
          <TradeItem key={`history-${index}`} order={item} onViewChange={isInView => loadMoreItems(isInView, index)} />
        ))}
      </div>
      {(isLoading || isFetchingNextPage) && <BlockLoading />}
      {!isLoading && !hasNextPage && orderList.length === 0 && <PositionNothing />}
      {!isLoading && !hasNextPage && orderList.length > 0 && <EndStatus />}
    </div>
  );
};
